import type { LegacyChatResult } from '@/types/legacyChat';

export const getString = (value: unknown) =>
  typeof value === 'string' ? value : '';

export const getQbankData = (data: LegacyChatResult) =>
  data.user_data?.qbank as Record<string, unknown> | null | undefined;

export const getTopicsData = (
  data: LegacyChatResult,
  key:
    | 'weak_topics'
    | 'strong_topics'
    | 'weak_topics_global'
    | 'strong_topics_global'
) =>
  Array.isArray(data.user_data?.[key])
    ? (data.user_data[key] as Array<Record<string, unknown>>)
    : [];

export const formatMetric = (value: unknown, suffix = '') =>
  value === null || value === undefined || value === ''
    ? '?'
    : `${value}${suffix}`;

const MAX_EXPANDED_REFS = 24;
const MAX_REF_INDEX = 250;

export const expandRefs = (body: string) => {
  if (body === 'X') return ['X'];
  const refs: string[] = [];

  body
    .split(/[,\s]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .forEach((part) => {
      const match = part.match(/^([AQFVDU])(\d+)(?::(\d+))?$/);
      if (!match) return;
      const prefix = match[1];
      const start = Number(match[2]);
      const rawEnd = match[3] ? Number(match[3]) : start;
      const end = Math.min(rawEnd, start + MAX_EXPANDED_REFS - 1);

      if (
        !Number.isInteger(start) ||
        !Number.isInteger(end) ||
        start < 1 ||
        start > MAX_REF_INDEX ||
        end < start
      ) {
        return;
      }

      for (let index = start; index <= end; index += 1) {
        if (refs.length >= MAX_EXPANDED_REFS) return;
        refs.push(`${prefix}${index}`);
      }
    });

  return refs;
};

export const lookupRef = (ref: string, data?: LegacyChatResult | null) => {
  if (!data) return null;
  if (ref === 'X') return data.anchor || null;
  const prefix = ref[0];
  const index = Number(ref.slice(1)) - 1;
  if (prefix === 'A') return data.search_results?.articles?.[index] || null;
  if (prefix === 'Q') return data.search_results?.questions?.[index] || null;
  if (prefix === 'F') return data.search_results?.flashcards?.[index] || null;
  if (prefix === 'V') return data.search_results?.videos?.[index] || null;
  if (prefix === 'D') return data.help_docs?.[index] || null;
  if (prefix === 'U') return { fact: `User fact ${index + 1}` };
  return null;
};

const STRAY_INLINE_HTML_PATTERN = /<\/?(b|strong|i|em)\s*>|<br\s*\/?>/i;

// V2 responses generated while raw legacy HTML history was fed to the model
// can contain stray inline HTML tags the model imitated. Map them onto
// markdown so the renderer shows emphasis instead of literal tags.
export const normalizeStrayInlineHtml = (text: string): string => {
  if (!STRAY_INLINE_HTML_PATTERN.test(text)) return text;

  return text
    .replace(/<\/?(b|strong)\s*>/gi, '**')
    .replace(/<\/?(i|em)\s*>/gi, '*')
    .replace(/<br\s*\/?>/gi, '\n');
};
